import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  TrendingUp, Clock, DollarSign, Shield, CheckCircle, ArrowRight,
  Loader2, LogIn, Sprout, Leaf, Tractor,
} from 'lucide-react';
import { InvestmentPlansSkeleton } from '../components/PageSkeletons';
import PaymentInstructions from '../components/PaymentInstructions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Investment = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [investor, setInvestor] = useState(null);
  const [paymentInstructions, setPaymentInstructions] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ amount: '' });

  useEffect(() => {
    fetchPlans();
    checkAuth();
    const interval = setInterval(fetchPlans, 60_000);
    const onFocus = () => fetchPlans();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const r = await axios.get(`${API}/investor/me`);
      if (r.data?.user) {
        setInvestor({ ...r.data.user, _account_type: 'investor' });
        return;
      }
    } catch { /* fall through */ }
    try {
      const r = await axios.get(`${API}/auth/me`);
      if (r.data?.user) {
        setInvestor({ ...r.data.user, _account_type: 'customer' });
        return;
      }
    } catch { /* not signed in */ }
    localStorage.removeItem('investor_data');
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/investments/plans`);
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    if (!investor) {
      toast.info('Please sign in to invest');
      navigate('/auth?redirect=/investment');
      return;
    }
    setSelectedPlan(plan);
    setFormData({ amount: plan.min_amount });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!investor) {
      toast.error('Please sign in to invest');
      navigate('/auth?redirect=/investment');
      return;
    }
    if (parseFloat(formData.amount) < selectedPlan.min_amount) {
      toast.error(`Minimum investment is ₦${selectedPlan.min_amount.toLocaleString()}`);
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/investments/apply`, {
        plan_id: selectedPlan.id,
        amount: parseFloat(formData.amount),
      });
      if (response.data.success) {
        toast.success('Investment application submitted!');
        setPaymentInstructions({
          ...response.data.payment_instructions,
          plan_name: selectedPlan.name || selectedPlan.id,
          duration_months: selectedPlan.duration_months,
          return_percentage: selectedPlan.return_percentage,
        });
        setShowForm(false);
        setSelectedPlan(null);
        setFormData({ amount: '' });
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit application';
      if (error.response?.status === 401) {
        toast.error('Please sign in to invest');
        navigate('/auth?redirect=/investment');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const calculateReturn = (amount, percentage) => amount * (percentage / 100);

  const features = [
    { icon: TrendingUp, title: 'Premium Returns',   body: 'Earn up to 50% fixed returns on select investment terms' },
    { icon: Shield,     title: 'Secure Investment', body: 'Your investment is backed by real farm assets' },
    { icon: Clock,      title: 'Flexible Terms',    body: 'Choose 6 or 12 month investment plans' },
    { icon: DollarSign, title: 'Easy Payout',       body: 'Receive returns directly to your bank account' },
  ];

  return (
    <div className="min-h-screen bg-textured-cream page-enter" data-testid="investment-page">
      {/* ═══ 1. HERO — Invest in Agriculture ═══ */}
      <section className="relative bg-canopy text-white overflow-hidden">
        {/* organic accent shapes */}
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
             style={{ background: 'radial-gradient(circle, #fbbf24, transparent 70%)' }} />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full opacity-15 blur-3xl"
             style={{ background: 'radial-gradient(circle, #22c55e, transparent 70%)' }} />

        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="eyebrow eyebrow-light" data-testid="hero-eyebrow">
              <Sprout className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              Sustainable Nigerian Agriculture
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold mt-6 mb-6 leading-[1.05]">
              Invest in the<br />
              <span style={{ color: '#fde68a' }}>ground that feeds us.</span>
            </h1>
            <p className="text-lg md:text-xl text-green-50/85 max-w-2xl mb-10 leading-relaxed">
              Grow your wealth with Daye Farm. Back real farmland, real harvests,
              and earn premium fixed returns from Nigeria&rsquo;s most vital sector.
            </p>

            {!investor ? (
              <div className="flex flex-wrap gap-3">
                <Link to="/auth?redirect=/investment">
                  <Button className="btn-gold px-8 py-6 text-base rounded-full" data-testid="hero-signup-btn">
                    <LogIn className="h-4 w-4 mr-2" /> Sign in to invest
                  </Button>
                </Link>
                <a href="#plans">
                  <Button
                    variant="outline"
                    className="px-8 py-6 text-base rounded-full bg-white/5 border-white/25 text-white hover:bg-white/15 hover:text-white backdrop-blur"
                    data-testid="hero-view-plans-btn"
                  >
                    View plans <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </div>
            ) : (
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-sm">
                <CheckCircle className="h-4 w-4 text-green-300" />
                Signed in as <strong>{investor.full_name || investor.email}</strong>
                <Link
                  to={investor._account_type === 'investor' ? '/investor/dashboard' : '/account'}
                  className="underline underline-offset-4 ml-1 hover:text-yellow-200"
                >
                  Dashboard
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══ 2. PERCENTAGE / RETURNS SECTION ═══ */}
      <section className="relative -mt-14 md:-mt-20 px-6 z-10">
        <div className="max-w-5xl mx-auto">
          <div className="premium-card p-8 md:p-12" data-testid="returns-section">
            <div className="text-center mb-10">
              <span className="eyebrow">Fixed Returns</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-4 text-gray-900">
                Predictable growth, rooted in real assets
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                Structured agricultural investment with professionally defined payout windows for medium- and long-term growth.
              </p>
              <hr className="divider-organic" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              {/* 25% card */}
              <div className="relative rounded-2xl p-8 border border-green-100 bg-gradient-to-br from-mist-green to-white overflow-hidden group"
                   style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)' }}
                   data-testid="return-card-6mo">
                <Leaf className="absolute -right-4 -bottom-4 h-32 w-32 text-green-100 group-hover:rotate-12 transition-transform duration-500" strokeWidth={1} />
                <div className="relative">
                  <p className="text-xs font-semibold tracking-widest uppercase text-green-700 mb-3">6-month plan</p>
                  <div className="stat-numeric text-7xl md:text-8xl text-green-800 font-bold">
                    25<span className="text-4xl text-green-600 align-top">%</span>
                  </div>
                  <p className="text-gray-600 mt-3 text-sm">Return on principal · paid at maturity</p>
                </div>
              </div>

              {/* 50% card — flagship */}
              <div className="relative rounded-2xl p-8 border border-amber-200 overflow-hidden group text-white"
                   style={{ background: 'linear-gradient(135deg, #14532d 0%, #15803d 60%, #d97706 130%)' }}
                   data-testid="return-card-12mo">
                <Sprout className="absolute -right-4 -bottom-4 h-32 w-32 text-white/10 group-hover:rotate-12 transition-transform duration-500" strokeWidth={1} />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-xs font-semibold tracking-widest uppercase text-yellow-200">12-month plan</p>
                    <span className="ribbon-gold text-[10px] font-bold px-2 py-0.5 rounded-full">FLAGSHIP</span>
                  </div>
                  <div className="stat-numeric text-7xl md:text-8xl font-bold">
                    50<span className="text-4xl text-yellow-200 align-top">%</span>
                  </div>
                  <p className="text-yellow-50/90 mt-3 text-sm">Highest return · full-year commitment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 3. INVESTMENT PLANS ═══ */}
      <section id="plans" className="py-24 px-6" data-testid="plans-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="eyebrow">Choose Your Plan</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-4 text-gray-900">
              Investment Plans
            </h2>
            <p className="text-gray-600 mt-4 max-w-xl mx-auto">
              Every plan is backed by farmland, livestock, and standing produce. Select the horizon that matches your capital goals.
            </p>
          </div>

          {loading ? (
            <InvestmentPlansSkeleton />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {plans.map((plan) => {
                const isFlagship = plan.duration_months === 12;
                return (
                  <div
                    key={plan.id}
                    className={`premium-card overflow-hidden relative ${isFlagship ? 'md:scale-[1.02]' : ''}`}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    {isFlagship && (
                      <div className="absolute top-5 right-5 ribbon-gold text-[10px] font-bold px-3 py-1.5 rounded-full z-10">
                        BEST VALUE
                      </div>
                    )}
                    <div
                      className="p-8 text-white relative overflow-hidden"
                      style={{
                        background: isFlagship
                          ? 'linear-gradient(135deg, #052e16 0%, #14532d 55%, #15803d 100%)'
                          : 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
                      }}
                    >
                      <Leaf className="absolute -right-6 -top-6 h-28 w-28 text-white/10" strokeWidth={1} />
                      <p className="text-xs font-semibold tracking-widest uppercase text-green-100 mb-2">
                        {plan.duration_months} months
                      </p>
                      <h3 className="font-display text-3xl font-semibold">{plan.name}</h3>
                      <div className="flex items-baseline gap-2 mt-5">
                        <span className="stat-numeric text-6xl font-bold">{plan.return_percentage}%</span>
                        <span className="text-green-100 text-sm">return</span>
                      </div>
                    </div>

                    <div className="p-8 bg-white">
                      <ul className="space-y-3.5 mb-8">
                        <li className="flex items-start gap-3 text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Duration: <strong>{plan.duration_months} months</strong></span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Minimum: <strong>₦{plan.min_amount?.toLocaleString()}</strong></span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Maximum: <strong>₦{plan.max_amount?.toLocaleString()}</strong></span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{plan.description}</span>
                        </li>
                      </ul>
                      <Button
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full py-6 rounded-full text-base font-semibold ${
                          isFlagship
                            ? 'btn-gold'
                            : 'bg-green-700 hover:bg-green-800 text-white'
                        }`}
                        data-testid={`invest-btn-${plan.id}`}
                      >
                        {investor ? 'Invest Now' : 'Sign in to invest'}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ═══ 4. FEATURES (moved to bottom) ═══ */}
      <section className="py-24 px-6 bg-parchment border-t border-green-100/60" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="eyebrow">Why Daye Farm</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold mt-4 text-gray-900">
              Built on trust, harvested with care
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Professional agricultural investing designed around transparency, security, and dependable returns.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="premium-card p-8 text-center"
                data-testid={`feature-card-${i}`}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #fef3c7 100%)' }}
                >
                  <Icon className="h-7 w-7 text-green-800" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-xl font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          {/* Closing CTA */}
          {!investor && (
            <div className="mt-16 text-center">
              <hr className="divider-organic" />
              <p className="text-gray-700 mb-6">Ready to grow with us?</p>
              <Link to="/auth?redirect=/investment">
                <Button className="btn-gold px-8 py-6 rounded-full text-base" data-testid="footer-cta-btn">
                  Create your investment account <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Investment Form Modal */}
      {showForm && selectedPlan && investor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             data-testid="invest-form-modal">
          <Card className="w-full max-w-md premium-card border-0">
            <CardHeader>
              <span className="eyebrow">New Investment</span>
              <CardTitle className="font-display text-2xl mt-2">Invest in {selectedPlan.name}</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Investing as <strong>{investor.full_name || investor.email}</strong>
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5 text-gray-700">Investment Amount (₦) *</label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    min={selectedPlan.min_amount}
                    max={selectedPlan.max_amount}
                    required
                    className="text-lg font-semibold h-12"
                    data-testid="invest-amount"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Min: ₦{selectedPlan.min_amount?.toLocaleString()} · Max: ₦{selectedPlan.max_amount?.toLocaleString()}
                  </p>
                </div>

                {formData.amount && (
                  <div className="rounded-xl p-5 border border-green-100"
                       style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #fefce8 100%)' }}>
                    <h4 className="font-semibold text-green-900 mb-3 text-sm tracking-wide">INVESTMENT SUMMARY</h4>
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Investment:</span>
                        <span className="font-semibold">₦{parseFloat(formData.amount || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expected Return:</span>
                        <span className="font-semibold text-green-700">
                          ₦{calculateReturn(parseFloat(formData.amount || 0), selectedPlan.return_percentage).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-green-200">
                        <span className="text-gray-700 font-medium">Total Payout:</span>
                        <span className="font-bold text-green-900">
                          ₦{(parseFloat(formData.amount || 0) + calculateReturn(parseFloat(formData.amount || 0), selectedPlan.return_percentage)).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-semibold">{selectedPlan.duration_months} months</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 btn-gold rounded-full" disabled={submitting} data-testid="invest-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Submit Investment
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {paymentInstructions && (
        <PaymentInstructions
          instructions={paymentInstructions}
          onClose={() => {
            setPaymentInstructions(null);
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
};

export default Investment;
